import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { BarChart } from "../../components/ui/bar-chart";
import { Card, ErrorState, Loading, PageHeader, StatCard, Toolbar } from "../../components/ui/misc";
import { formatDuration, formatNumber } from "../../lib/format";
import { analyticsOverviewQuery, onlineNowQuery } from "../../lib/queries";
import { RangePicker, resolveRange } from "./range";

export function AnalyticsPage() {
  const search = useSearch({ from: "/authed/analytics" });
  const navigate = useNavigate({ from: "/analytics" });
  const range = resolveRange(search);
  const overview = useQuery(analyticsOverviewQuery(range));
  const online = useQuery(onlineNowQuery);

  if (overview.error)
    return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;

  return (
    <>
      <PageHeader
        title="Analytics"
        description="Server-measured views, launches and play time. Sessions are attributed on the API, never by the browser."
      />

      <Toolbar>
        <RangePicker range={range} onChange={(next) => void navigate({ search: next })} />
        <span className="text-muted text-caption">
          {range.from} → {range.to}
        </span>
      </Toolbar>

      {overview.isPending ? (
        <Loading />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Views" value={formatNumber(overview.data.totals.views)} />
            <StatCard label="Launches" value={formatNumber(overview.data.totals.launches)} />
            <StatCard label="Play time" value={formatDuration(overview.data.totals.playSeconds)} />
            <StatCard
              label="Playing now"
              value={formatNumber(online.data?.totalSessions ?? 0)}
              hint={online.data?.games[0] ? `Top: ${online.data.games[0].name}` : undefined}
            />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Card title="Launches per day">
              <BarChart
                points={overview.data.daily.map((point) => ({
                  label: point.day,
                  value: point.launches,
                }))}
              />
            </Card>
            <Card title="Play time per day">
              <BarChart
                points={overview.data.daily.map((point) => ({
                  label: point.day,
                  value: point.playSeconds,
                }))}
              />
            </Card>
          </div>

          <Card title="Top games" className="mt-4">
            <table className="text-ui w-full border-collapse text-left">
              <thead className="bg-well">
                <tr className="border-line border-b">
                  <th className="text-micro text-faint h-9 pr-3 font-semibold uppercase">Game</th>
                  <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Views</th>
                  <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">
                    Launches
                  </th>
                  <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">
                    Play time
                  </th>
                  <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">
                    Avg session
                  </th>
                </tr>
              </thead>
              <tbody>
                {overview.data.topGames.map((game) => (
                  <tr key={game.gameId} className="t-row border-line/60 border-b last:border-0">
                    <td className="h-9 pr-3">
                      <Link
                        to="/analytics/games/$gameId"
                        params={{ gameId: String(game.gameId) }}
                        search={range}
                        className="text-ink hover:text-accent"
                      >
                        {game.name}
                      </Link>
                    </td>
                    <td className="tabular px-3">{formatNumber(game.views)}</td>
                    <td className="tabular px-3">{formatNumber(game.launches)}</td>
                    <td className="tabular px-3">{formatDuration(game.playSeconds)}</td>
                    <td className="tabular px-3">{formatDuration(game.avgSessionSeconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </>
  );
}
